import {
    Box,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material'
import { DataGrid, GridColDef, GridRowSelectionModel, GridActionsCellItem } from '@mui/x-data-grid'
import { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ApartmentOutlined, FilterAltOff, RemoveCircleOutline, Search } from '@mui/icons-material'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { HttpGroup } from 'src/http/group/httpGroup'
import { GroupSectorComercialViewDTO, GroupSectorComercialViewDTOFields } from 'src/types/group/groupData'

const opcionAsignados = { value: 1, label: 'Asignados', color: 'success' as const }
const opcionTodos = { value: 2, label: 'Todos', color: 'info' as const }
const toggleOptions = [opcionAsignados, opcionTodos]

export default function EditSectorComercialesGrupo({ onClose, grupo }: { onClose: () => void; grupo: number }) {
    const [sectores, setSectores] = useState<GroupSectorComercialViewDTO[]>()
    const [allSectores, setAllSectores] = useState<GroupSectorComercialViewDTO[]>()
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>()
    const [activoFilter, setActivoFilter] = useState<number[]>([opcionAsignados.value])

    const { control, watch, reset: resetForm } = useForm<{ nombre: string }>({
        defaultValues: { nombre: '' }
    })

    const nombreFilter = watch('nombre')

    const columnas: GridColDef<GroupSectorComercialViewDTO>[] = [
        {
            headerName: 'Sector comercial',
            field: GroupSectorComercialViewDTOFields.NombreSectorComercial,
            flex: 3
        },
        {
            field: 'actions',
            type: 'actions',
            width: 60,
            getActions: ({ row }) => {
                const id = row[GroupSectorComercialViewDTOFields.CodSectorComercial]
                const isAssigned = (rowSelectionModel ?? []).includes(id)

                if (!isAssigned) return []

                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title='Quitar del grupo'>
                                <RemoveCircleOutline fontSize='small' />
                            </Tooltip>
                        }
                        label='Quitar'
                        onClick={() => setRowSelectionModel(prev => (prev ?? []).filter(x => x !== id))}
                        color='error'
                    />
                ]
            }
        }
    ]

    useEffect(() => {
        HttpGroup.getSectoresComercialPorGrupo(grupo).then(data => {
            setAllSectores(data)
            const selected = data
                .filter(s => s[GroupSectorComercialViewDTOFields.PerteneceASector])
                .map(s => s[GroupSectorComercialViewDTOFields.CodSectorComercial])
            setRowSelectionModel(prev => [...new Set([...selected, ...(prev || [])])])
        })
    }, [])

    // Filtrar client-side
    useEffect(() => {
        if (!allSectores) return

        let filtered = allSectores

        // Filtro Asignados/Todos
        const soloAsignados = activoFilter.includes(opcionAsignados.value) && !activoFilter.includes(opcionTodos.value)
        if (soloAsignados) {
            filtered = filtered.filter(s => s[GroupSectorComercialViewDTOFields.PerteneceASector])
        }

        // Filtro por nombre
        if (nombreFilter) {
            const lower = nombreFilter.toLowerCase()
            filtered = filtered.filter(s =>
                s[GroupSectorComercialViewDTOFields.NombreSectorComercial]?.toLowerCase().includes(lower)
            )
        }

        setSectores(filtered)
    }, [allSectores, activoFilter, nombreFilter])

    const handleActivoChange = (_: any, newValue: number | null) => {
        if (newValue === null) return
        setActivoFilter([newValue])
    }

    const handleClearFilters = () => {
        resetForm({ nombre: '' })
        setActivoFilter([opcionAsignados.value])
    }

    const onSubmitWrapper = () => {
        let mensajeError = 'No se pudieron guardar los sectores'

        const promise = HttpGroup
            .updateSectoresComercialPorGrupo(grupo, [...((rowSelectionModel as number[]) || [])])
            .then(() => {
                setTimeout(onClose, 500)
            })
            .catch(err => {
                mensajeError = err?.message || mensajeError
                throw err
            })

        toast.promise(promise, {
            success: 'Sectores actualizados',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' spacing={1.5} alignItems='center'>
                        <ApartmentOutlined color='primary' />
                        <Typography variant='h6' fontWeight={600}>
                            Sectores Comerciales del Grupo
                        </Typography>
                    </Stack>
                    <Stack direction='row' spacing={1}>
                        <Button variant='text' color='inherit' onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                            Guardar
                        </Button>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Barra de filtros */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'action.hover'
                    }}
                >
                    <Controller
                        control={control}
                        name='nombre'
                        render={({ field }) => (
                            <TextField
                                {...field}
                                value={field.value ?? ''}
                                label='Sector comercial'
                                size='small'
                                sx={{ minWidth: 200, flex: 1 }}
                                InputProps={{
                                    startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                                }}
                            />
                        )}
                    />
                    <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={activoFilter[0] ?? null}
                        onChange={handleActivoChange}
                    >
                        {toggleOptions.map(option => (
                            <ToggleButton key={option.value} value={option.value} color={option.color}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Tooltip title='Limpiar filtros'>
                        <IconButton size='small' onClick={handleClearFilters}>
                            <FilterAltOff fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Grilla */}
                <Box sx={{ height: '100%' }}>
                    <DataGrid
                        disableColumnFilter
                        disableColumnMenu
                        keepNonExistentRowsSelected
                        columns={columnas}
                        checkboxSelection
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={setRowSelectionModel}
                        rows={sectores ?? []}
                        getRowId={row => row[GroupSectorComercialViewDTOFields.CodSectorComercial]}
                        density='compact'
                        pagination
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 }
                            }
                        }}
                        pageSizeOptions={[25, 50, 75, 100]}
                    />
                </Box>
            </DialogContent>
        </>
    )
}
